import { REGULAR_CREDIT_TYPE, toCreditBenefit } from './credits-api'
import { ensureCourseConsent } from './course-consent'
import { format } from 'date-fns'

import { api, ApiError, formData, type Schemas } from '@/api/client'
import { authEvents } from '@/lib/auth-events'
import { DEFAULT_TABLE_PAGE_SIZE } from '@/lib/constants'
import { optimizeCourseImage } from '@/lib/image-optimization'
import type {
  Booking,
  BookingStatus,
  Course,
  CourseLevel,
  CourseSession,
  Profile,
  PublicSession,
  SubscriptionPackage,
  AdminSubscriptionPackage,
  AdminSubscriptionPackageInput,
  MemberSubscriptionPriceStatus,
  TrainerCourse,
  UserRole,
  UserSubscription,
  ManagedUserBooking,
  ManagedUserPackage,
  UserPurchasedPackage,
  PaymentEntry,
  ManagedUserPaymentEntry,
  ManagedUserCreditAdjustment,
  VoucherCode,
  VoucherCodeMutationInput,
  VoucherCodeUpdateInput,
  VoucherRedemptionResult,
  VoucherRedemptionReportItem,
  Discount,
  DiscountMutationInput,
  DiscountUpdateInput,
  DiscountMemberAssignment,
  MemberDiscountAssignment,
  HonorariumTier,
  HonorariumNote,
  HonorariumNoteStatus,
  PurchasedPackageReportItem,
  TokenUsageReportItem,
  AttendanceReportItem,
  FinancialReportItem,
  TrainerActivityReportItem,
  UserGrowthReport,
  PaginatedResponse,
  PublicCourseCatalogCourse,
  Resource,
  ResourceCategory,
  NotificationTemplate,
  SessionRegistrationSettings,
  SessionCancellationSettings,
  CalendarTagFilterMode,
  SessionBookingQuote,
  SessionMoveConflict,
} from '@/types/domain'

type SortDirection = 'asc' | 'desc'
type UserListSortKey = 'name' | 'email' | 'role' | 'status' | 'created_at'
type BookingListSortKey = 'session' | 'name' | 'email' | 'status'
type UserStatusFilter = 'all' | 'active' | 'inactive'
type PaginatedResult<T> = {
  items: T[]
  total: number
}

export type AdminProfilesQueryParams = {
  searchTerm: string
  roleFilter: 'all' | UserRole
  statusFilter: UserStatusFilter
  sortKey: UserListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}

export type AdminBookingsQueryParams = {
  statusFilter: BookingStatus | 'all'
  courseIdFilter: string | 'all'
  searchTerm: string
  sortKey: BookingListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}

function mapUserSubscription(row: Record<string, unknown>): UserSubscription {
  const remainingCredits = Number(row.remaining_credits ?? 0)
  const maxSessionsPerWeek =
    row.max_sessions_per_week == null ? null : Number(row.max_sessions_per_week)
  const billingMode =
    row.billing_mode === 'subscription' || (row.billing_mode == null && Boolean(row.is_active))
      ? 'subscription'
      : 'payment'

  return {
    user_id: String(row.user_id),
    package_type: String(row.package_type) as UserSubscription['package_type'],
    package_name: String(row.package_name),
    billing_mode: billingMode,
    remaining_credits: remainingCredits,
    max_sessions_per_week: maxSessionsPerWeek,
    expires_at: (row.expires_at as string | null) ?? null,
    is_active: Boolean(row.is_active),
    stripe_status: (row.stripe_status as string | null) ?? null,
    current_period_end: (row.current_period_end as string | null) ?? null,
    stripe_cancel_at_period_end: Boolean(row.stripe_cancel_at_period_end),
    stripe_cancel_at: (row.stripe_cancel_at as string | null) ?? null,
    stripe_canceled_at: (row.stripe_canceled_at as string | null) ?? null,
    subscription_price_cents:
      row.subscription_price_cents == null ? null : Number(row.subscription_price_cents),
    subscription_currency: String(row.subscription_currency ?? 'eur'),
    stripe_price_id: (row.stripe_price_id as string | null) ?? null,
    catalog_price_cents:
      row.catalog_price_cents == null ? null : Number(row.catalog_price_cents),
    catalog_stripe_price_id: (row.catalog_stripe_price_id as string | null) ?? null,
    price_status:
      (row.price_status as UserSubscription['price_status']) ?? null,
    price_update_error: (row.price_update_error as string | null) ?? null,
  }
}



function buildFullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim()
}

export async function getPublicSessions(from?: string, to?: string): Promise<PublicSession[]> {
  const { data } = await api.GET('/api/v1/public/sessions', {
    params: {
      query: {
        from: from ?? new Date().toISOString(),
        to: to ?? new Date(Date.now() + 1000 * 60 * 60 * 24 * 90).toISOString(),
      },
    },
  })
  return data!.map(toPublicSession)
}

function toPublicSession(row: Schemas['PublicSessionResponse']): PublicSession {
  return { ...row, level: row.level as CourseLevel }
}

export async function getMyCalendarSelectedTags(): Promise<string[]> {
  const { data } = await api.GET('/api/v1/users/me/calendar-tags')
  return data!.selected_tags
}

export async function saveMyCalendarSelectedTags(selectedTags: string[]): Promise<void> {
  await api.PUT('/api/v1/users/me/calendar-tags', { body: { selected_tags: selectedTags } })
}

export async function getPublicCourseCatalog(): Promise<PublicCourseCatalogCourse[]> {
  const { data } = await api.GET('/api/v1/public/courses')
  return data!.map((row) => ({ ...row, level: row.level as CourseLevel }))
}

export async function getPublicSessionById(sessionId: string): Promise<PublicSession> {
  const { data } = await api.GET('/api/v1/public/sessions/{id}', { params: { path: { id: sessionId } } })
  return toPublicSession(data!)
}





export async function getSubscriptionPackages(): Promise<SubscriptionPackage[]> {
  const { data } = await api.GET('/api/v1/subscription-packages')
  return data!.map((row) => ({
    ...row,
    applied_discount_type: row.applied_discount_type as SubscriptionPackage['applied_discount_type'],
    billing_mode: row.billing_mode as SubscriptionPackage['billing_mode'],
    features: [...row.features],
    benefits: row.benefits.map(toCreditBenefit),
  }))
}

export async function getAdminSubscriptionPackages(): Promise<AdminSubscriptionPackage[]> {
  const { data } = await api.GET('/api/v1/subscription-packages/all')
  return data!.map((row) => ({
    ...row,
    billing_mode: row.billing_mode as AdminSubscriptionPackage['billing_mode'],
    features: [...row.features],
    benefits: row.benefits.map(toCreditBenefit),
  }))
}

export async function saveAdminSubscriptionPackage(
  payload: AdminSubscriptionPackageInput,
): Promise<{ queued: number }> {
  const body = {
    name: payload.name,
    billing_mode: payload.billingMode,
    price_cents: payload.priceCents,
    stripe_price_id: payload.stripePriceId ?? null,
    credits_included: payload.creditsIncluded,
    max_sessions_per_week: payload.maxSessionsPerWeek,
    features: payload.features,
    display_order: payload.displayOrder,
    is_featured: payload.isFeatured,
    is_active: payload.isActive,
    apply_price_to_existing: payload.applyPriceToExisting ?? false,
    benefits: payload.benefits ?? null,
    required_course_id: payload.requiredCourseId ?? null,
    confirmation: payload.confirmation ?? null,
  }
  const { data } = payload.id
    ? await api.PUT('/api/v1/subscription-packages/{id}', { params: { path: { id: payload.id } }, body })
    : await api.POST('/api/v1/subscription-packages', { body })
  return { queued: data!.queued }
}

export async function archiveAdminSubscriptionPackage(packageId: string): Promise<void> {
  await api.POST('/api/v1/subscription-packages/{id}/archive', { params: { path: { id: packageId } } })
}

export async function deleteAdminSubscriptionPackage(packageId: string): Promise<void> {
  await api.DELETE('/api/v1/subscription-packages/{id}', { params: { path: { id: packageId } } })
}

export async function reorderAdminSubscriptionPackages(packageIds: string[]): Promise<void> {
  await api.PUT('/api/v1/subscription-packages/order', { body: { package_ids: packageIds } })
}

export async function retrySubscriptionPriceUpdates(packageId?: string): Promise<void> {
  await api.POST('/api/v1/subscription-packages/price-update-retries', { body: { package_id: packageId ?? null } })
}

export async function rolloutAdminSubscriptionPackagePrice(
  { packageId, confirmation }: { packageId: string; confirmation: string },
): Promise<{ queued: number }> {
  const { data } = await api.POST('/api/v1/subscription-packages/{id}/price-rollouts', {
    params: { path: { id: packageId } },
    body: { confirmation },
  })
  return { queued: data!.queued }
}

export async function updateManagedUserSubscriptionPrice(userId: string): Promise<void> {
  await api.POST('/api/v1/users/{id}/subscription-price-updates', { params: { path: { id: userId } } })
}

export async function getAdminMemberSubscriptionPriceStatuses(
  userIds: string[],
): Promise<MemberSubscriptionPriceStatus[]> {
  if (userIds.length === 0) {
    return []
  }

  const { data } = await api.GET('/api/v1/subscription-packages/member-price-statuses', {
    params: { query: { user_ids: userIds } },
  })
  return data!.map((row) => ({
    ...row,
    price_status: row.price_status as MemberSubscriptionPriceStatus['price_status'],
  }))
}

export async function getUserSubscriptionStatus(): Promise<UserSubscription | null> {
  const { data } = await api.GET('/api/v1/users/me/subscription')
  // 204 when the member has neither a subscription nor regular credits.
  return data ? mapUserSubscription(data as unknown as Record<string, unknown>) : null
}

export async function getUserPurchasedPackages(): Promise<UserPurchasedPackage[]> {
  const { data } = await api.GET('/api/v1/users/me/credit-packages')
  return data!.map(toPurchasedPackage)
}

function toPurchasedPackage(row: Schemas['PurchasedPackageEntry']): ManagedUserPackage {
  return {
    ...row,
    package_type: row.package_type as ManagedUserPackage['package_type'],
    billing_mode: row.billing_mode as ManagedUserPackage['billing_mode'],
    credit_type_id: row.credit_type_id ?? undefined,
    credit_name: row.credit_name ?? undefined,
    remaining_credits: row.remaining_credits ?? 0,
    entry_kind: row.entry_kind as ManagedUserPackage['entry_kind'],
  }
}

export async function getUserPaymentEntries(): Promise<PaymentEntry[]> {
  const { data } = await api.GET('/api/v1/users/me/payment-entries')
  return data!.map(toPaymentEntry)
}

function toPaymentEntry(row: Schemas['PaymentEntryResponse']): PaymentEntry {
  return {
    ...row,
    package_type: row.package_type as PaymentEntry['package_type'],
    payment_kind: row.payment_kind as PaymentEntry['payment_kind'],
    payment_provider: row.payment_provider as PaymentEntry['payment_provider'],
    document_kind: row.document_kind as PaymentEntry['document_kind'],
  }
}

export async function redeemVoucherCode(code: string): Promise<VoucherRedemptionResult> {
  const { data } = await api.POST('/api/v1/voucher-redemptions', { body: { code } })
  return data!
}

export async function getVoucherCodes(): Promise<VoucherCode[]> {
  const { data } = await api.GET('/api/v1/vouchers')
  return data!
}

export async function generateVoucherCode(): Promise<string> {
  const { data } = await api.GET('/api/v1/vouchers/generated-code')
  return data!.code
}

export async function createVoucherCode(payload: VoucherCodeMutationInput): Promise<VoucherCode> {
  const { data } = await api.POST('/api/v1/vouchers', {
    body: {
      credit_type_id: payload.creditTypeId ?? REGULAR_CREDIT_TYPE,
      credits: payload.credits,
      max_redemptions: payload.maxRedemptions,
      code: payload.code?.trim() || null,
    },
  })
  return data!
}

export async function updateVoucherCode(payload: VoucherCodeUpdateInput): Promise<VoucherCode> {
  const { data } = await api.PUT('/api/v1/vouchers/{id}', {
    params: { path: { id: payload.id } },
    body: {
      credit_type_id: payload.creditTypeId ?? REGULAR_CREDIT_TYPE,
      credits: payload.credits,
      max_redemptions: payload.maxRedemptions,
      code: payload.code?.trim() || '',
    },
  })
  return data!
}

export async function deleteVoucherCode(voucherId: string): Promise<void> {
  await api.DELETE('/api/v1/vouchers/{id}', { params: { path: { id: voucherId } } })
}

export async function getDiscounts(): Promise<Discount[]> {
  const { data } = await api.GET('/api/v1/discounts')
  return data!.map(toDiscount)
}

function toDiscount(row: Schemas['DiscountResponse']): Discount {
  return { ...row, discount_type: row.discount_type as Discount['discount_type'] }
}

export async function createDiscount(payload: DiscountMutationInput): Promise<Discount> {
  const { data } = await api.POST('/api/v1/discounts', {
    body: {
      title: payload.title,
      discount_type: payload.discountType,
      percent_off: payload.discountType === 'percent' ? payload.percentOff ?? null : null,
      amount_cents: payload.discountType === 'amount' ? payload.amountCents ?? null : null,
      is_active: true,
    },
  })
  return toDiscount(data!.discount)
}

export async function updateDiscount(payload: DiscountUpdateInput): Promise<Discount> {
  const { data } = await api.PUT('/api/v1/discounts/{id}', {
    params: { path: { id: payload.id } },
    body: {
      title: payload.title,
      discount_type: payload.discountType,
      percent_off: payload.discountType === 'percent' ? payload.percentOff ?? null : null,
      amount_cents: payload.discountType === 'amount' ? payload.amountCents ?? null : null,
      is_active: payload.isActive ?? true,
    },
  })
  return toDiscount(data!.discount)
}

export async function deleteDiscount(discountId: string): Promise<void> {
  await api.DELETE('/api/v1/discounts/{id}', { params: { path: { id: discountId } } })
}

export async function getMemberDiscountAssignments(
  userId: string,
): Promise<MemberDiscountAssignment[]> {
  const { data } = await api.GET('/api/v1/users/{id}/discounts', { params: { path: { id: userId } } })
  return data!.map(toMemberDiscountAssignment)
}

function toMemberDiscountAssignment(row: Schemas['MemberDiscountResponse']): MemberDiscountAssignment {
  return { ...row, discount_type: row.discount_type as MemberDiscountAssignment['discount_type'] }
}

export async function getDiscountMemberAssignments(
  discountId: string,
): Promise<DiscountMemberAssignment[]> {
  const { data } = await api.GET('/api/v1/discounts/{id}/members', { params: { path: { id: discountId } } })
  return data!
}

export async function manageMemberDiscount(payload: {
  userId: string
  discountId: string
  action: 'assign' | 'remove'
}): Promise<MemberDiscountAssignment[]> {
  const params = { path: { id: payload.userId, discountId: payload.discountId } }
  const { data } = payload.action === 'assign'
    ? await api.PUT('/api/v1/users/{id}/discounts/{discountId}', { params })
    : await api.DELETE('/api/v1/users/{id}/discounts/{discountId}', { params })
  return data!.assignments.map(toMemberDiscountAssignment)
}

export async function getManagedUserProfile(userId: string): Promise<Profile> {
  const { data } = await api.GET('/api/v1/users/{id}', { params: { path: { id: userId } } })
  return toProfile(data!)
}

export async function getManagedUserBookings(userId: string): Promise<ManagedUserBooking[]> {
  const { data } = await api.GET('/api/v1/users/{id}/bookings', { params: { path: { id: userId } } })
  return data!.map((row) => ({
    ...row,
    status: row.status as BookingStatus,
    booking_source: row.booking_source as ManagedUserBooking['booking_source'],
  }))
}

export async function getSessionCancellationSettings(): Promise<SessionCancellationSettings> {
  const { data } = await api.GET('/api/v1/settings/session-cancellation-hours')
  return data!
}

export async function getSessionRegistrationSettings(): Promise<SessionRegistrationSettings> {
  const { data } = await api.GET('/api/v1/settings/session-registration-hours')
  return data!
}

export async function updateSessionRegistrationSettings(
  hours: number,
): Promise<SessionRegistrationSettings> {
  const { data } = await api.PUT('/api/v1/settings/session-registration-hours', {
    body: { hours: Math.max(0, Math.trunc(hours)) },
  })
  return data!
}

export async function updateSessionCancellationSettings(
  hours: number,
): Promise<SessionCancellationSettings> {
  const { data } = await api.PUT('/api/v1/settings/session-cancellation-hours', {
    body: { hours: Math.max(0, Math.trunc(hours)) },
  })
  return data!
}

export async function getCalendarTagFilterMode(): Promise<CalendarTagFilterMode> {
  const { data } = await api.GET('/api/v1/settings/calendar-tag-filter-mode')
  return data!.mode === 'popover' ? 'popover' : 'buttons'
}

export async function updateCalendarTagFilterMode(
  mode: CalendarTagFilterMode,
): Promise<CalendarTagFilterMode> {
  const { data } = await api.PUT('/api/v1/settings/calendar-tag-filter-mode', { body: { mode } })
  return data!.mode === 'popover' ? 'popover' : 'buttons'
}

export async function getManagedUserPackages(userId: string): Promise<ManagedUserPackage[]> {
  const { data } = await api.GET('/api/v1/users/{id}/credit-packages', { params: { path: { id: userId } } })
  return data!.map(toPurchasedPackage)
}

export async function getManagedUserPaymentEntries(
  userId: string,
): Promise<ManagedUserPaymentEntry[]> {
  const { data } = await api.GET('/api/v1/users/{id}/payment-entries', { params: { path: { id: userId } } })
  return data!.map(toPaymentEntry)
}

export async function getManagedUserSubscriptionStatus(
  userId: string,
): Promise<UserSubscription | null> {
  const { data } = await api.GET('/api/v1/users/{id}/subscription', { params: { path: { id: userId } } })
  return data ? mapUserSubscription(data as unknown as Record<string, unknown>) : null
}

export async function adjustManagedUserCredits(payload: {
  creditTypeId?: string
  userId: string
  delta: number
  reason?: string
  amountPaidCents?: number | null
  operationKey?: string
}): Promise<{ total_credits: number }> {
  if (payload.delta > 0 && (payload.amountPaidCents ?? 0) > 0) {
    // The operation key makes a retry after a lost response neither bill nor book twice.
    const { data } = await api.POST('/api/v1/users/{id}/credit-invoices', {
      params: { path: { id: payload.userId } },
      body: {
        credit_type_id: payload.creditTypeId ?? REGULAR_CREDIT_TYPE,
        credits: payload.delta,
        reason: payload.reason ?? null,
        amount_paid_cents: payload.amountPaidCents ?? 0,
        operation_key: payload.operationKey ?? crypto.randomUUID(),
      },
    })
    return { total_credits: data!.total_credits }
  }

  const { data } = await api.POST('/api/v1/users/{id}/credit-adjustments', {
    params: { path: { id: payload.userId } },
    body: {
      credit_type_id: payload.creditTypeId ?? REGULAR_CREDIT_TYPE,
      delta: payload.delta,
      reason: payload.reason ?? null,
    },
  })
  return { total_credits: data!.total_credits }
}

export async function getManagedUserCreditAdjustments(
  userId: string,
  options?: { limit?: number; offset?: number },
): Promise<ManagedUserCreditAdjustment[]> {
  const { data } = await api.GET('/api/v1/users/{id}/credit-adjustments', {
    params: { path: { id: userId }, query: { limit: options?.limit ?? 200, offset: options?.offset ?? 0 } },
  })
  return data!.map((row) => ({ ...row, reason: row.reason ?? '' }))
}

export async function bookManagedUserSession(payload: {
  userId: string
  sessionId: string
}): Promise<void> {
  await api.POST('/api/v1/users/{id}/bookings', {
    params: { path: { id: payload.userId } },
    body: { session_id: payload.sessionId },
  })
}

export async function cancelManagedUserBooking(payload: {
  userId: string
  bookingId: string
}): Promise<void> {
  await api.POST('/api/v1/users/{id}/bookings/{bookingId}/cancellation', {
    params: { path: { id: payload.userId, bookingId: payload.bookingId } },
  })
}

export async function bookSessionWithCredit(sessionId: string): Promise<void> {
  await ensureCourseConsent({ sessionId })
  await api.POST('/api/v1/bookings', { body: { session_id: sessionId } })
}



export async function getSessionBookingQuote(sessionId: string): Promise<SessionBookingQuote> {
  const { data } = await api.GET('/api/v1/sessions/{id}/booking-quote', { params: { path: { id: sessionId } } })
  return toSessionBookingQuote(data!)
}

function toSessionBookingQuote(row: Schemas['SessionBookingQuoteResponse']): SessionBookingQuote {
  return { ...row, credit_type_id: row.credit_type_id ?? undefined, credit_name: row.credit_name ?? undefined }
}

export async function getManagedUserSessionBookingQuote(payload: {
  userId: string
  sessionId: string
}): Promise<SessionBookingQuote> {
  const { data } = await api.GET('/api/v1/sessions/{id}/booking-quote', {
    params: { path: { id: payload.sessionId }, query: { userId: payload.userId } },
  })
  return toSessionBookingQuote(data!)
}

export async function createSubscriptionCheckoutSession(packageType: string): Promise<{
  sessionId: string
  url: string
}> {
  const { data } = await api.POST('/api/v1/checkout-sessions', { body: { package_type: packageType } })
  return { sessionId: data!.session_id, url: data!.url }
}

export async function createCustomerPortalSession(): Promise<{ url: string }> {
  const { data } = await api.POST('/api/v1/customer-portal-sessions')
  return { url: data!.url }
}

export async function createInvoiceDownloadUrl(stripeInvoiceId: string): Promise<{ url: string }> {
  const { data } = await api.GET('/api/v1/invoices/{invoiceId}/pdf-url', { params: { path: { invoiceId: stripeInvoiceId } } })
  return { url: data!.url }
}

export async function getMemberBookings(params: {
  upcomingOnly?: boolean
}): Promise<Booking[]> {
  const { data } = await api.GET('/api/v1/users/me/bookings', {
    params: { query: { upcomingOnly: params.upcomingOnly ?? false } },
  })

  return data!.map((row) => ({
    id: row.booking_id,
    session_id: row.session_id,
    full_name: row.full_name,
    email: row.email,
    status: row.status as BookingStatus,
    created_at: row.created_at,
    confirmed_at: row.confirmed_at,
    cancelled_at: row.cancelled_at,
    consumed_credit: row.balance_credits_used > 0,
    credits_required: row.credits_required,
    subscription_credits_used: row.subscription_credits_used,
    balance_credits_used: row.balance_credits_used,
    booking_source: row.booking_source as Booking['booking_source'],
    course_sessions: {
      id: row.session_id,
      starts_at: row.starts_at,
      ends_at: row.ends_at,
      max_participants: row.max_participants,
      min_participants: row.min_participants,
      confirmed_count: row.confirmed_count,
      registration_until: row.registration_until,
      cancellation_until: row.cancellation_until,
      trainer_id: row.trainer_id,
      trainer_name: row.trainer_name,
      resource_id: row.resource_id,
      resource_name: row.resource_name,
      courses: {
        name: row.course_name,
        description: row.course_description,
        level: row.course_level,
        credits_per_session: row.credits_required,
      },
    },
  }))
}

export async function cancelMemberBooking(bookingId: string): Promise<void> {
  await api.POST('/api/v1/bookings/{id}/cancellation', { params: { path: { id: bookingId } } })
}

export async function signInUser(payload: {
  email: string
  password: string
  captchaToken?: string
}): Promise<void> {
  const { data } = await api.POST('/api/v1/auth/login', {
    body: { email: payload.email, password: payload.password },
  })
  authEvents.setUser(data!.id)
}

export async function signUpUser(payload: {
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber: string
  company?: string
  captchaToken?: string
}): Promise<{ requiresEmailConfirmation: boolean }> {
  const phoneNumber = payload.phoneNumber.trim()
  if (!phoneNumber) {
    throw new Error('Bitte gib deine Telefonnummer ein.')
  }

  const { data } = await api.POST('/api/v1/auth/registration', {
    body: {
      email: payload.email,
      password: payload.password,
      first_name: payload.firstName,
      last_name: payload.lastName,
      phone_number: phoneNumber,
      company: payload.company?.trim() || null,
    },
  })
  return { requiresEmailConfirmation: data!.requires_email_confirmation }
}

export async function signOutUser(): Promise<void> {
  await api.POST('/api/v1/auth/logout')
  authEvents.setUser(null)
}

export async function sendPasswordResetEmail(email: string): Promise<void> {
  await api.POST('/api/v1/auth/password-reset-requests', { body: { email } })
}

/** Sets a new password with the token from a reset or invitation link. */
export async function resetPasswordWithToken(payload: {
  email: string
  token: string
  password: string
}): Promise<void> {
  await api.POST('/api/v1/auth/password-resets', { body: payload })
}

export async function confirmEmail(payload: { email: string; token: string }): Promise<void> {
  await api.POST('/api/v1/auth/email-confirmations', { body: payload })
}

export async function resendEmailConfirmation(email: string): Promise<void> {
  await api.POST('/api/v1/auth/email-confirmation-requests', { body: { email } })
}

export async function updateUserPassword(currentPassword: string, password: string): Promise<void> {
  await api.PUT('/api/v1/auth/password', { body: { current_password: currentPassword, password } })
}

export async function getCurrentUserRole(): Promise<Profile | null> {
  try {
    const { data } = await api.GET('/api/v1/auth/me')
    authEvents.setUser(data!.id)
    return toProfile(data!)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }
    throw error
  }
}

export async function getCourses(includeInactive = true): Promise<Course[]> {
  const { data } = await api.GET('/api/v1/courses', { params: { query: { includeInactive } } })
  return data!.map((row) => ({
    ...row,
    level: row.level as CourseLevel,
    trainers: row.trainers.map((trainer) => ({ ...trainer, role: trainer.role as UserRole })),
  }))
}

export async function getProfilesPaginated(params: {
  roles: UserRole[]
  searchTerm: string
  roleFilter: 'all' | UserRole
  statusFilter: UserStatusFilter
  sortKey: UserListSortKey
  sortDirection: SortDirection
  page: number
  pageSize: number
}): Promise<PaginatedResult<Profile>> {
  const staffOnly = !params.roles.includes('member')
  const membersOnly = params.roles.every((role) => role === 'member')
  return listUsers(staffOnly ? 'staff' : membersOnly ? 'customers' : 'all', params)
}

async function listUsers(
  scope: Schemas['UserListScope'],
  params: AdminProfilesQueryParams,
): Promise<PaginatedResult<Profile>> {
  const { data } = await api.GET('/api/v1/users', {
    params: {
      query: {
        scope,
        search: params.searchTerm || undefined,
        role: params.roleFilter,
        status: params.statusFilter,
        sort: params.sortKey,
        direction: params.sortDirection,
        page: params.page,
        page_size: params.pageSize,
      },
    },
  })
  return { items: data!.items.map(toProfile), total: data!.total }
}

function toProfile(row: Schemas['MeResponse'] | Schemas['UserProfileResponse']): Profile {
  return {
    ...row,
    role: row.role as UserRole,
    email: row.email ?? '',
  }
}

export async function getTrainer(id: string): Promise<Profile | null> {
  try {
    const { data } = await api.GET('/api/v1/trainers/{id}', { params: { path: { id } } })
    return toProfile(data!)
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    throw error
  }
}

export async function getTrainerPublicSessions(
  trainerId: string,
  from: Date,
  to: Date
): Promise<PublicSession[]> {
  const { data } = await api.GET('/api/v1/trainers/{id}/public-sessions', {
    params: { path: { id: trainerId }, query: { from: from.toISOString(), to: to.toISOString() } },
  })
  return data!.map(toPublicSession)
}

export async function getTrainerCourses(trainerId: string): Promise<TrainerCourse[]> {
  const { data } = await api.GET('/api/v1/trainers/{id}/courses', { params: { path: { id: trainerId } } })
  return data!.map((row) => ({ ...row, level: row.level as CourseLevel }))
}

export async function getTrainerProfiles(): Promise<Profile[]> {
  const { data } = await api.GET('/api/v1/trainers')
  return data!.map((row) => ({
    id: row.id,
    role: 'trainer' as UserRole,
    first_name: row.first_name,
    last_name: row.last_name ?? '',
    full_name: row.full_name,
    email: '',
    is_active: true,
    birth_date: null,
    street: null,
    house_number: null,
    postal_code: null,
    city: null,
    phone_number: null,
    company: null,
    avatar_url: null,
    created_at: '',
  }))
}

export async function getStaffProfiles(
  params: AdminProfilesQueryParams,
): Promise<PaginatedResult<Profile>> {
  return listUsers('staff', params)
}

export async function getCustomerProfiles(
  params: AdminProfilesQueryParams,
): Promise<PaginatedResult<Profile>> {
  return listUsers('customers', params)
}

export async function updateProfileByAdmin(
  id: string,
  payload: {
    email?: string
    first_name: string
    last_name: string
    role: UserRole
    is_active: boolean
    birth_date?: string | null
    street?: string | null
    house_number?: string | null
    postal_code?: string | null
    city?: string | null
    phone_number?: string | null
    company?: string | null
    iban?: string | null
    bank_name?: string | null
    avatar_url?: string | null
  },
): Promise<void> {
  await api.PATCH('/api/v1/users/{id}', {
    params: { path: { id } },
    body: {
      ...payload,
      email: payload.email ?? null,
      full_name: buildFullName(payload.first_name, payload.last_name),
    },
  })
}

export async function createUserByAdmin(payload: {
  email: string
  first_name: string
  last_name: string
  role: UserRole
  createMode: 'invite' | 'password'
  password?: string
  birth_date?: string | null
  street?: string | null
  house_number?: string | null
  postal_code?: string | null
  city?: string | null
  phone_number?: string | null
  company?: string | null
  iban?: string | null
  bank_name?: string | null
  avatar_url?: string | null
}): Promise<void> {
  const { createMode, ...fields } = payload
  await api.POST('/api/v1/users', {
    body: {
      ...fields,
      create_mode: createMode,
      password: payload.password ?? null,
      full_name: buildFullName(payload.first_name, payload.last_name),
    },
  })
}

export async function deleteUserByAdmin(id: string): Promise<void> {
  await api.DELETE('/api/v1/users/{id}', { params: { path: { id } } })
}

export async function updateOwnProfile(payload: {
  first_name: string
  last_name: string
  birth_date?: string | null
  street?: string | null
  house_number?: string | null
  postal_code?: string | null
  city?: string | null
  phone_number?: string | null
  company?: string | null
  avatar_url?: string | null
}): Promise<void> {
  await api.PUT('/api/v1/users/me', { body: payload })
}





export async function createCourse(payload: {
  name: string
  description: string
  level: CourseLevel
  color: string
  duration_minutes: number
  credits_per_session: number
  default_max_participants: number
  default_min_participants: number | null
  trainer_ids: string[]
  is_active: boolean
  image_url?: string | null
  tags: string[]
}): Promise<string> {
  // Course and trainers are saved in one transaction.
  const { data } = await api.POST('/api/v1/courses', { body: payload })
  return data!.id
}

export async function updateCourse(
  id: string,
  payload: {
    name: string
    description: string
    level: CourseLevel
    color: string
    duration_minutes: number
    credits_per_session: number
    default_max_participants: number
    default_min_participants: number | null
    trainer_ids: string[]
    is_active: boolean
    image_url?: string | null
    tags: string[]
  },
): Promise<void> {
  await api.PUT('/api/v1/courses/{id}', { params: { path: { id } }, body: payload })
}

export async function deleteOrDeactivateCourse(id: string): Promise<'deleted' | 'deactivated'> {
  const { data } = await api.DELETE('/api/v1/courses/{id}', { params: { path: { id } } })
  return data!.result === 'deleted' ? 'deleted' : 'deactivated'
}

export async function saveWeekAsTemplate(startDate: string): Promise<number> {
  const { data } = await api.PUT('/api/v1/week-template', { body: { start_date: startDate } })
  return data!.count
}

export async function applyTemplateToWeek(
  targetStartDate: string,
  overwrite: boolean = false,
): Promise<number> {
  const { data } = await api.POST('/api/v1/week-template/applications', {
    body: { target_start_date: targetStartDate, overwrite },
  })
  return data!.count
}

export async function getSessions(range: { from: string; to: string }): Promise<CourseSession[]> {
  const { data } = await api.GET('/api/v1/sessions', { params: { query: { from: range.from, to: range.to } } })
  return data!.map(toCourseSession)
}

function toCourseSession(row: Schemas['SessionResponse']): CourseSession {
  return {
    ...row,
    courses: { ...row.courses, level: row.courses.level as CourseLevel },
    trainer: row.trainer ?? undefined,
    resource: (row.resource ?? undefined) as Resource | undefined,
    report: row.report
      ? { ...row.report, substitute_trainer: row.report.substitute_trainer ?? undefined }
      : undefined,
  }
}

export async function createSession(payload: {
  course_id: string
  trainer_id: string | null
  resource_id: string | null
  starts_at: string
  ends_at: string
  max_participants: number
  min_participants: number | null
  is_cancelled: boolean
  registration_until: string | null
  cancellation_until: string | null
}): Promise<void> {
  await api.POST('/api/v1/sessions', { body: payload })
}

// Members whose booking is not regularly covered at the new start time. The move
// itself is never blocked; the result only informs the editor.
export async function previewSessionMove(sessionId: string, startsAt: string): Promise<SessionMoveConflict[]> {
  const { data } = await api.GET('/api/v1/sessions/{id}/move-preview', {
    params: { path: { id: sessionId }, query: { startsAt } },
  })
  return data!.map((row) => ({ ...row, reason: row.reason as SessionMoveConflict['reason'] }))
}

export async function updateSession(
  id: string,
  payload: {
    course_id: string
    trainer_id: string | null
    resource_id: string | null
    starts_at: string
    ends_at: string
    max_participants: number
    min_participants: number | null
    is_cancelled: boolean
    registration_until: string | null
    cancellation_until: string | null
  },
): Promise<void> {
  await api.PUT('/api/v1/sessions/{id}', { params: { path: { id } }, body: payload })
}

export async function deleteSession(id: string): Promise<void> {
  await api.DELETE('/api/v1/sessions/{id}', { params: { path: { id } } })
}

export async function getConfirmedBookingCountsBySession(): Promise<Record<string, number>> {
  const { data } = await api.GET('/api/v1/sessions/booking-counts')
  return data!
}


export async function getBookings(
  params: AdminBookingsQueryParams,
): Promise<PaginatedResult<Booking>> {
  const { data } = await api.GET('/api/v1/bookings', {
    params: {
      query: {
        status: params.statusFilter === 'all' ? undefined : params.statusFilter,
        course_id: params.courseIdFilter === 'all' ? undefined : params.courseIdFilter,
        search: params.searchTerm.trim() || undefined,
        sort: params.sortKey,
        direction: params.sortDirection,
        page: params.page,
        page_size: params.pageSize,
      },
    },
  })

  return {
    items: data!.items.map((row) => ({
      ...row,
      status: row.status as BookingStatus,
      booking_source: row.booking_source as Booking['booking_source'],
      course_sessions: {
        id: row.session.id,
        starts_at: row.session.starts_at,
        ends_at: row.session.ends_at,
        max_participants: row.session.max_participants,
        min_participants: row.session.min_participants,
        confirmed_count: 0,
        registration_until: null,
        cancellation_until: null,
        trainer_id: null,
        trainer_name: null,
        resource_id: row.session.resource_id,
        resource_name: row.session.resource_name,
        courses: {
          name: row.session.course_name,
          description: row.session.course_description,
          level: null,
          credits_per_session: row.session.credits_per_session,
        },
      },
    })),
    total: data!.total,
  }
}

export async function cancelBookingAsAdmin(id: string): Promise<void> {
  await api.POST('/api/v1/bookings/{id}/cancellation', { params: { path: { id } } })
}

export async function getTrainerSessions(
  trainerId: string,
  range: { from: string; to: string },
): Promise<CourseSession[]> {
  const { data } = await api.GET('/api/v1/sessions', {
    params: { query: { from: range.from, to: range.to, trainerId } },
  })
  return data!.map(toCourseSession)
}

export async function getSessionBookings(sessionId: string): Promise<Booking[]> {
  const { data } = await api.GET('/api/v1/sessions/{id}/bookings', { params: { path: { id: sessionId } } })
  return data!.map((row) => ({ ...row, status: row.status as BookingStatus }))
}

export async function updateSessionReport(
  sessionId: string,
  payload: {
    actual_duration_minutes: number
    trainer_notes: string
    substitute_trainer_id: string | null
  },
): Promise<void> {
  await api.PUT('/api/v1/sessions/{id}/report', { params: { path: { id: sessionId } }, body: payload })
}

export async function uploadAvatar(file: File): Promise<string> {
  const { data } = await api.POST('/api/v1/uploads/avatars', {
    body: formData({ file }) as never,
  })
  return data!.url
}

export async function uploadCourseImage(file: File): Promise<string> {
  // Resized and converted to WebP in the browser before upload.
  const optimizedFile = await optimizeCourseImage(file)
  const { data } = await api.POST('/api/v1/uploads/course-images', {
    body: formData({ file: optimizedFile }) as never,
  })
  return data!.url
}

export async function getHonorariumTiers(trainerId: string | null): Promise<HonorariumTier[]> {
  if (!trainerId) {
    return []
  }

  const { data } = await api.GET('/api/v1/trainers/{trainerId}/honorarium-tiers', { params: { path: { trainerId } } })
  return data!
}

export async function updateHonorariumTiers(
  trainerId: string,
  tiers: Omit<HonorariumTier, 'id' | 'created_at' | 'updated_at'>[],
): Promise<void> {
  await api.PUT('/api/v1/trainers/{trainerId}/honorarium-tiers', {
    params: { path: { trainerId } },
    body: {
      tiers: tiers.map((tier) => ({
        min_participants: tier.min_participants,
        max_participants: tier.max_participants,
        amount_cents: tier.amount_cents,
      })),
    },
  })
}

export async function getBillableSessions(
  trainerId: string,
  startDate: Date,
  endDate: Date,
): Promise<(CourseSession & { booking_count: number; amount_cents: number; is_billed: boolean })[]> {
  const { data } = await api.GET('/api/v1/trainers/{trainerId}/billable-sessions', {
    params: { path: { trainerId }, query: { from: format(startDate, 'yyyy-MM-dd'), to: format(endDate, 'yyyy-MM-dd') } },
  })
  return data!.map((s) => ({
    id: s.id,
    course_id: s.course_id,
    starts_at: s.starts_at,
    ends_at: s.ends_at,
    courses: { name: s.course_name, level: s.course_level },
    report: s.actual_duration_minutes == null ? null : { actual_duration_minutes: s.actual_duration_minutes },
    is_billed: s.is_billed,
    booking_count: s.booking_count,
    amount_cents: s.amount_cents,
  })) as unknown as (CourseSession & { booking_count: number; amount_cents: number; is_billed: boolean })[]
}

export async function createHonorariumNote(payload: {
  trainerId: string
  periodStart: Date
  periodEnd: Date
  items: { session_id: string; participant_count: number; amount_cents: number }[]
}): Promise<HonorariumNote> {
  // Participants and amounts are computed by the server when the note is created.
  const { data } = await api.POST('/api/v1/honorarium-notes', {
    body: {
      trainer_id: payload.trainerId,
      period_start: format(payload.periodStart, 'yyyy-MM-dd'),
      period_end: format(payload.periodEnd, 'yyyy-MM-dd'),
      session_ids: [...new Set(payload.items.map((item) => item.session_id))],
    },
  })
  return toHonorariumNote(data!)
}

function toHonorariumNote(row: Schemas['HonorariumNoteResponse']): HonorariumNote {
  return { ...row, status: row.status as HonorariumNoteStatus }
}

export async function getHonorariumNotes(trainerId: string): Promise<HonorariumNote[]> {
  const { data } = await api.GET('/api/v1/trainers/{trainerId}/honorarium-notes', { params: { path: { trainerId } } })
  return data!.map(toHonorariumNote)
}

/** Downloads the honorarium note as PDF (rendered by the server). */
export async function downloadHonorariumNotePdf(noteId: string): Promise<void> {
  const { data, response } = await api.GET('/api/v1/honorarium-notes/{id}/pdf', { params: { path: { id: noteId } }, parseAs: 'blob' })
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const fileName = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? 'Honorarnote.pdf'
  const url = URL.createObjectURL(data as Blob)
  const link = document.createElement('a')
  link.href = url
  link.download = decodeURIComponent(fileName)
  link.click()
  URL.revokeObjectURL(url)
}

/** Mails the PDF to the trainer; a note that was sent before is only mailed again with `resend`. */
export async function sendHonorariumNote(noteId: string, resend: boolean): Promise<void> {
  await api.POST('/api/v1/honorarium-notes/{id}/email', { params: { path: { id: noteId } }, body: { resend } })
}

export async function updateHonorariumNoteStatus(
  noteId: string,
  status: HonorariumNoteStatus,
): Promise<void> {
  await api.PATCH('/api/v1/honorarium-notes/{id}', { params: { path: { id: noteId } }, body: { status } })
}

export async function deleteHonorariumNote(noteId: string): Promise<void> {
  await api.DELETE('/api/v1/honorarium-notes/{id}', { params: { path: { id: noteId } } })
}
export async function getAdminPurchasedPackagesReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<PurchasedPackageReportItem>> {
  const { data } = await api.GET('/api/v1/reports/package-payments', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items.map((row) => ({ ...row, package_type: row.package_type as PurchasedPackageReportItem['package_type'] })), total: data!.total }
}

export async function getAdminTokenUsageReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<TokenUsageReportItem>> {
  const { data } = await api.GET('/api/v1/reports/token-usage', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items, total: data!.total }
}

export async function getAdminVoucherRedemptionsReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<VoucherRedemptionReportItem>> {
  const { data } = await api.GET('/api/v1/reports/voucher-redemptions', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items, total: data!.total }
}

export async function getAdminAttendanceReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<AttendanceReportItem>> {
  const { data } = await api.GET('/api/v1/reports/attendance', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items, total: data!.total }
}

export async function getAdminFinancialReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<FinancialReportItem>> {
  const { data } = await api.GET('/api/v1/reports/financial', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items.map((row) => ({ ...row, package_type: row.package_type as FinancialReportItem['package_type'] })), total: data!.total }
}

export async function getAdminTrainerActivityReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<TrainerActivityReportItem>> {
  const { data } = await api.GET('/api/v1/reports/trainer-activity', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items.map((row) => ({ ...row, full_name: row.full_name ?? '' })), total: data!.total }
}

export async function getAdminUserGrowthReport(params: {
  from: string
  to: string
  page?: number
  pageSize?: number
}): Promise<PaginatedResponse<UserGrowthReport>> {
  const { data } = await api.GET('/api/v1/reports/user-growth', {
    params: { query: { from: params.from, to: params.to, page: params.page ?? 1, page_size: params.pageSize ?? DEFAULT_TABLE_PAGE_SIZE } },
  })
  return { data: data!.items, total: data!.total }
}

export async function getResourceCategories(): Promise<ResourceCategory[]> {
  const { data } = await api.GET('/api/v1/resource-categories')
  return data!.map(toResourceCategory)
}

function toResourceCategory(row: Schemas['ResourceCategoryResponse']): ResourceCategory {
  return { ...row, visual_type: row.visual_type as ResourceCategory['visual_type'] }
}

export async function createResourceCategory(payload: {
  name: string
  visual_type: string
  config: unknown
}): Promise<ResourceCategory> {
  const { data } = await api.POST('/api/v1/resource-categories', { body: payload })
  return toResourceCategory(data!)
}

export async function updateResourceCategory(
  id: string,
  payload: {
    name: string
    visual_type: string
    config: unknown
  },
): Promise<void> {
  await api.PUT('/api/v1/resource-categories/{id}', { params: { path: { id } }, body: payload })
}

export async function deleteResourceCategory(id: string): Promise<void> {
  await api.DELETE('/api/v1/resource-categories/{id}', { params: { path: { id } } })
}

export async function getResources(categoryId?: string): Promise<Resource[]> {
  const { data } = await api.GET('/api/v1/resources', { params: { query: { categoryId } } })
  return data!.map((row) => ({ ...row, category: row.category ? toResourceCategory(row.category) : undefined }))
}

export async function upsertResource(resource: Partial<Resource>): Promise<void> {
  const body = {
    category_id: resource.category_id ?? null,
    name: resource.name ?? null,
    is_active: resource.is_active ?? true,
    order_index: resource.order_index ?? 0,
    x: resource.x ?? 0,
    y: resource.y ?? 0,
    width: resource.width ?? 100,
    height: resource.height ?? 100,
  }
  if (resource.id) {
    await api.PUT('/api/v1/resources/{id}', { params: { path: { id: resource.id } }, body })
  } else {
    await api.POST('/api/v1/resources', { body })
  }
}

export async function deleteResource(id: string): Promise<void> {
  await api.DELETE('/api/v1/resources/{id}', { params: { path: { id } } })
}

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  const { data } = await api.GET('/api/v1/notification-templates')
  return data!.map((row) => ({ ...row, variables: [...row.variables] }))
}

export async function createNotificationTemplate(
  payload: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>,
): Promise<NotificationTemplate> {
  const { data } = await api.POST('/api/v1/notification-templates', { body: payload })
  return { ...data!, variables: [...data!.variables] }
}

export async function updateNotificationTemplate(
  templateId: string,
  payload: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>,
): Promise<NotificationTemplate> {
  const { data } = await api.PUT('/api/v1/notification-templates/{id}', { params: { path: { id: templateId } }, body: payload })
  return { ...data!, variables: [...data!.variables] }
}

export async function getManualNotificationRecipients(payload: {
  userIds?: string[]
  roles?: string[]
  sessionId?: string
}): Promise<{ recipient_name: string | null; recipient_email: string }[]> {
  const { data } = await api.POST('/api/v1/notifications/recipient-previews', {
    body: {
      user_ids: payload.userIds && payload.userIds.length > 0 ? payload.userIds : null,
      roles: payload.roles && payload.roles.length > 0 ? payload.roles : null,
      session_id: payload.sessionId || null,
    },
  })
  return data!
}

export async function enqueueManualNotification(payload: {
  templateSlug: string
  userIds?: string[]
  roles?: string[]
  sessionId?: string
  customSubject?: string
  customBody?: string
  isBulk?: boolean
}): Promise<number> {
  const { data } = await api.POST('/api/v1/notifications', {
    body: {
      template_slug: payload.templateSlug,
      user_ids: payload.userIds && payload.userIds.length > 0 ? payload.userIds : null,
      roles: payload.roles && payload.roles.length > 0 ? payload.roles : null,
      session_id: payload.sessionId || null,
      custom_subject: payload.customSubject || null,
      custom_body: payload.customBody || null,
      is_bulk: payload.isBulk ?? false,
    },
  })
  return data!.queued
}

